import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { type Location } from "@src/appV2/redesign/utils/routes";
import { startTransition } from "react";
import { useHistory } from "react-router-dom";

import { Badge } from "../../../components/Badge";
import { Icon, type IconType } from "../../../components/Icon";
import { Text } from "../../../components/Text";
import { applyPlatformAwareInteractionStyles } from "../../../utils/applyPlatformAwareInteractionStyles";

interface NavigationMenuItemMobileProps {
  children: React.ReactNode;
  iconType: IconType;
  isSelected: boolean;
  routeTo?: Location;
  onClick?: () => void;
  badgeCount?: number;
}

export function NavigationMenuItemMobile(props: NavigationMenuItemMobileProps) {
  const { children, iconType, isSelected, routeTo, onClick, badgeCount } = props;

  const history = useHistory();

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      padding={3}
      marginTop={2}
      gap={3}
      sx={(theme) => ({
        borderRadius: theme.borderRadius?.medium,
        cursor: "pointer",
        transition: theme.transitions.create(["background-color"], {
          duration: theme.transitions.duration.short,
        }),

        color: isSelected
          ? theme.palette.navbar?.itemContentSelected
          : theme.palette.navbar?.itemContent,

        backgroundColor: isSelected ? theme.palette.navbar?.itemSelectedBackground : "transparent",

        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            backgroundColor: theme.palette.navbar?.itemSelectedBackground,
            color: theme.palette.navbar?.itemContentSelected,
          },

          defaultStyle: {
            backgroundColor: isSelected
              ? theme.palette.navbar?.itemSelectedBackground
              : "transparent",

            color: isSelected
              ? theme.palette.navbar?.itemContentSelected
              : theme.palette.navbar?.itemContent,
          },
        }),
      })}
      onClick={async () => {
        startTransition(() => {
          if (isDefined(routeTo)) {
            history.push(routeTo);
          }

          onClick?.();
        });
      }}
    >
      <Box display="flex" flexDirection="row" alignItems="center" gap={3}>
        <Icon
          type={iconType}
          size="large"
          sx={{
            flexShrink: 0,
          }}
        />

        <Text
          bold
          variant="body1"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          overflow="hidden"
          sx={{
            color: "inherit",
            lineHeight: 1,
          }}
        >
          {children}
        </Text>
      </Box>

      {isDefined(badgeCount) && badgeCount > 0 && (
        <Box sx={{ flexShrink: 0, marginRight: 3 }}>
          <Badge count={badgeCount} size="small" max={99} />
        </Box>
      )}
    </Box>
  );
}
